import { useContext, useEffect, useRef, useState } from "react"
import { Helmet } from "react-helmet-async"
import { useNavigate } from "react-router-dom"
import Modal from "react-responsive-modal"
import BreadCrumbs from "../components/BreadCrumbs"
import DeviceList from "../components/DeviceList"
import SettingRow, {
    SettingsSection,
    SegmentedControl,
} from "../components/shared/SettingRow"
import VicinityContext from "../context/vicinityContext"
import { usePreferences } from "../hooks/usePreferences"
import {
    getUserById,
    logoutAllDevices,
    signOutLocally,
    uploadAvatar,
} from "../services/userService"
import { buildFullName } from "../utilities/helpers"
import { announceProfilePicture } from "../utilities/profilePicture"
import {
    setMapStyle,
    setLocationRefreshMinutes,
} from "../utilities/preferences"
import { LOCALSTORAGE_LOCATIONPERMISSION } from "../const/storage"
import {
    MAP_STYLES,
    LOCATION_REFRESH_OPTIONS_MINUTES,
    LOCATION_PERMISSION_OPTIONS,
} from "../const/preferences"
import { version } from "../../package.json"
import "../css/Settings.css"

const AVATAR_TYPES = ["image/jpeg", "image/png", "image/webp"]
const AVATAR_MAX_BYTES = 5 * 1024 * 1024

const crumbs = [
    { path: "/", name: "Chaperon" },
    { path: "/settings", name: "Settings" },
]

const readStoredUser = () => {
    try {
        return JSON.parse(localStorage.getItem("chaperon_user_data") || "null")
    } catch {
        return null
    }
}

const capitalize = (value) =>
    value ? value.charAt(0).toUpperCase() + value.slice(1) : value

const formatDate = (value, withTime = false) => {
    if (!value) return ""
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ""
    return withTime
        ? date.toLocaleString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
          })
        : date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
          })
}

function DetailRow({ label, value, tone }) {
    return (
        <div className="setting-detail">
            <span className="setting-detail-label">{label}</span>
            <span
                className={`setting-detail-value${
                    tone ? ` text-${tone}` : ""
                }`}
            >
                {value || "-"}
            </span>
        </div>
    )
}

export default function Settings() {
    const navigate = useNavigate()
    const { notifications, setShowNotificationModal, setLocationPermission } =
        useContext(VicinityContext)
    const { mapStyle, locationRefreshMinutes } = usePreferences()

    const [user, setUser] = useState(readStoredUser)
    const [isLoadingUser, setIsLoadingUser] = useState(true)
    const [loadFailed, setLoadFailed] = useState(false)
    const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
    const [avatarError, setAvatarError] = useState("")
    const avatarInputRef = useRef(null)

    const [sharing, setSharing] = useState(
        () => localStorage.getItem(LOCALSTORAGE_LOCATIONPERMISSION) || "ask",
    )
    const [browserGeo, setBrowserGeo] = useState(null)

    const [showSignOutAll, setShowSignOutAll] = useState(false)
    const [isSigningOutAll, setIsSigningOutAll] = useState(false)
    const [signOutAllError, setSignOutAllError] = useState("")

    useEffect(() => {
        const storedId = readStoredUser()?.id
        if (!storedId) {
            setIsLoadingUser(false)
            setLoadFailed(true)
            return
        }

        let cancelled = false
        getUserById(storedId)
            .then((response) => {
                if (cancelled) return
                if (response.user) {
                    setUser(response.user)
                } else {
                    setLoadFailed(true)
                }
            })
            .catch(() => {
                if (!cancelled) setLoadFailed(true)
            })
            .finally(() => {
                if (!cancelled) setIsLoadingUser(false)
            })

        return () => {
            cancelled = true
        }
    }, [])

    // Mirrors the browser's own geolocation permission, so a "Allow" that the
    // browser has blocked is visible here instead of silently doing nothing.
    useEffect(() => {
        if (!navigator.permissions?.query) return

        let status = null
        let cancelled = false
        navigator.permissions
            .query({ name: "geolocation" })
            .then((result) => {
                if (cancelled) return
                status = result
                setBrowserGeo(result.state)
                result.onchange = () => setBrowserGeo(result.state)
            })
            .catch(() => {})

        return () => {
            cancelled = true
            if (status) status.onchange = null
        }
    }, [])

    const displayName =
        buildFullName(user?.first_name, user?.last_name) ||
        user?.username ||
        "Chaperon user"
    const planLabel = capitalize(user?.subscription_plan) || "Free"
    const planStatus = capitalize(user?.subscription_status)
    const unreadCount = (notifications || []).filter(
        (n) =>
            (n.status === "pending" || n.status === "unread") &&
            n.channel === "in_app",
    ).length

    const handleAvatarSelected = async (event) => {
        const file = event.target.files?.[0]
        event.target.value = ""
        if (!file) return

        if (!AVATAR_TYPES.includes(file.type)) {
            setAvatarError("Please choose a JPEG, PNG or WebP image.")
            return
        }
        if (file.size > AVATAR_MAX_BYTES) {
            setAvatarError("That image is over 5 MB. Please choose a smaller one.")
            return
        }

        setAvatarError("")
        setIsUploadingAvatar(true)
        try {
            const response = await uploadAvatar(file)
            if (response.url) {
                setUser((current) => ({
                    ...current,
                    profile_picture_url: response.url,
                }))
                announceProfilePicture(
                    user?.id ?? readStoredUser()?.id,
                    response.url,
                )
            }
        } catch (error) {
            setAvatarError(error.message || "Couldn't upload that image.")
        } finally {
            setIsUploadingAvatar(false)
        }
    }

    const handleSharingChange = (value) => {
        setSharing(value)
        setLocationPermission(value)
    }

    const sharingSub = () => {
        if (sharing === "deny") return { text: "Off. Chaperon won't use your location." }
        if (sharing === "ask") {
            return { text: "Chaperon will ask again the next time you visit." }
        }
        if (browserGeo === "denied") {
            return {
                text: "Limited: your browser is blocking location for this site.",
                tone: "danger",
            }
        }
        if (browserGeo === "prompt") {
            return { text: "Waiting for your browser's permission." }
        }
        return { text: "On. Your location updates while Chaperon is open." }
    }

    const handleSignOut = () => {
        signOutLocally()
        navigate("/")
    }

    const handleSignOutAll = async () => {
        setIsSigningOutAll(true)
        setSignOutAllError("")
        try {
            await logoutAllDevices()
            signOutLocally()
            navigate("/user/login")
        } catch (error) {
            setIsSigningOutAll(false)
            setSignOutAllError(
                error.message ||
                    "Couldn't sign out of all devices. Please try again.",
            )
        }
    }

    const closeSignOutAll = () => {
        if (isSigningOutAll) return
        setShowSignOutAll(false)
        setSignOutAllError("")
    }

    const sharingInfo = sharingSub()
    const refreshDisabled = sharing !== "allow"

    return (
        <div className="settings-page">
            <Helmet>
                <title>Chaperon - Settings</title>
            </Helmet>

            <BreadCrumbs crumbs={crumbs} />

            <div className="container settings-container py-3">
                <h1 className="settings-title">Settings</h1>

                {/* Profile card */}
                <div className="settings-card settings-profile">
                    <div className="settings-avatar-wrap">
                        <button
                            type="button"
                            className="settings-avatar"
                            onClick={() => avatarInputRef.current?.click()}
                            disabled={isUploadingAvatar}
                            aria-label="Change profile picture"
                        >
                            {user?.profile_picture_url ? (
                                <img
                                    src={user.profile_picture_url}
                                    alt={displayName}
                                />
                            ) : (
                                <span>
                                    {displayName.slice(0, 2).toUpperCase()}
                                </span>
                            )}
                            {isUploadingAvatar && (
                                <span className="settings-avatar-busy">
                                    <span
                                        className="spinner-border spinner-border-sm text-light"
                                        role="status"
                                        aria-label="Uploading"
                                    ></span>
                                </span>
                            )}
                        </button>
                        <span className="settings-avatar-badge" aria-hidden="true">
                            <i className="bi bi-camera-fill"></i>
                        </span>
                        <input
                            ref={avatarInputRef}
                            type="file"
                            accept={AVATAR_TYPES.join(",")}
                            className="d-none"
                            onChange={handleAvatarSelected}
                        />
                    </div>

                    <div className="settings-profile-text">
                        {isLoadingUser && !user ? (
                            <span className="placeholder-glow">
                                <span className="placeholder col-8"></span>
                            </span>
                        ) : (
                            <>
                                <div className="settings-profile-name">
                                    {displayName}
                                </div>
                                {user?.username && (
                                    <div className="settings-profile-handle">
                                        @{user.username}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <span className="settings-plan-pill">{planLabel}</span>
                </div>
                {avatarError && (
                    <div className="alert alert-danger py-2 mt-2" role="alert">
                        {avatarError}
                    </div>
                )}
                {loadFailed && (
                    <div className="alert alert-warning py-2 mt-2" role="alert">
                        Some profile details couldn't be loaded right now.
                    </div>
                )}

                <SettingsSection id="settings-profile" title="Profile">
                    <DetailRow label="First name" value={user?.first_name} />
                    <DetailRow label="Surname" value={user?.last_name} />
                    <DetailRow label="Email" value={user?.email} />
                    {user?.phone_number && (
                        <DetailRow label="Phone" value={user.phone_number} />
                    )}
                    <DetailRow
                        label="Status"
                        value={capitalize(user?.status)}
                        tone={
                            user?.status?.toLowerCase() === "active"
                                ? "success"
                                : undefined
                        }
                    />
                    <DetailRow
                        label="Member since"
                        value={formatDate(user?.created_at)}
                    />
                    <DetailRow
                        label="Last login"
                        value={formatDate(user?.last_login_at, true)}
                    />
                </SettingsSection>

                <SettingsSection id="settings-account" title="Account">
                    <SettingRow
                        icon="bi-star"
                        label="Subscription"
                        sub={
                            planStatus
                                ? `${planLabel} plan · ${planStatus}`
                                : `${planLabel} plan`
                        }
                        to="/pricing"
                    />
                    <SettingRow
                        icon="bi-bell"
                        label="Notification center"
                        sub={
                            unreadCount > 0
                                ? `${unreadCount} unread`
                                : "You're all caught up"
                        }
                        onClick={() => setShowNotificationModal(true)}
                    />
                </SettingsSection>

                <SettingsSection id="settings-preferences" title="Preferences">
                    <SettingRow
                        icon="bi-map"
                        label="Map style"
                        sub="How the community map is drawn"
                        right={
                            <SegmentedControl
                                name="map-style"
                                label="Map style"
                                options={MAP_STYLES}
                                value={mapStyle}
                                onChange={setMapStyle}
                            />
                        }
                    />
                </SettingsSection>

                <SettingsSection id="settings-location" title="Location sharing">
                    <SettingRow
                        icon="bi-geo-alt"
                        label="Share my location"
                        sub={sharingInfo.text}
                        subTone={sharingInfo.tone}
                        right={
                            <SegmentedControl
                                name="location-sharing"
                                label="Share my location"
                                options={LOCATION_PERMISSION_OPTIONS}
                                value={sharing}
                                onChange={handleSharingChange}
                            />
                        }
                    />
                    <SettingRow
                        icon="bi-arrow-repeat"
                        label="Location refresh interval"
                        sub={
                            refreshDisabled
                                ? "Available once location sharing is allowed"
                                : `Every ${locationRefreshMinutes} minutes while Chaperon is open`
                        }
                        right={
                            <select
                                className="form-select form-select-sm"
                                aria-label="Location refresh interval"
                                value={locationRefreshMinutes}
                                disabled={refreshDisabled}
                                onChange={(event) =>
                                    setLocationRefreshMinutes(
                                        Number(event.target.value),
                                    )
                                }
                            >
                                {LOCATION_REFRESH_OPTIONS_MINUTES.map(
                                    (minutes) => (
                                        <option key={minutes} value={minutes}>
                                            {minutes} min
                                        </option>
                                    ),
                                )}
                            </select>
                        }
                    />
                </SettingsSection>

                <SettingsSection id="settings-devices" title="Devices">
                    <DeviceList />
                    <SettingRow
                        icon="bi-shield-lock"
                        label="Sign out of all devices"
                        sub="Ends every session, including this one"
                        onClick={() => setShowSignOutAll(true)}
                        danger
                    />
                </SettingsSection>

                <SettingsSection id="settings-help" title="Help & support">
                    <SettingRow
                        icon="bi-life-preserver"
                        label="Help & support"
                        to="/contact"
                    />
                    <SettingRow
                        icon="bi-shield-check"
                        label="Privacy policy"
                        to="/privacy"
                    />
                    <SettingRow
                        icon="bi-file-text"
                        label="Terms of use"
                        to="/terms"
                    />
                    <SettingRow
                        icon="bi-box-arrow-right"
                        label="Sign out"
                        onClick={handleSignOut}
                        danger
                    />
                </SettingsSection>

                <p className="settings-footer">
                    Chaperon Web · v{version}
                    <br />
                    We've got you covered.
                </p>
            </div>

            <Modal
                open={showSignOutAll}
                onClose={closeSignOutAll}
                center
                classNames={{ modal: "customModal" }}
            >
                <h4 className="mb-3">
                    <i className="bi bi-exclamation-triangle text-warning pe-2"></i>
                    Sign out of all devices
                </h4>
                <p>
                    This signs your account out everywhere, including this
                    browser and the mobile app. You'll need to sign in again on
                    each device.
                </p>
                {signOutAllError && (
                    <div className="text-danger small mb-2">
                        {signOutAllError}
                    </div>
                )}
                <div className="d-flex gap-2 justify-content-end mt-4">
                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={handleSignOutAll}
                        disabled={isSigningOutAll}
                    >
                        {isSigningOutAll ? "Signing out..." : "Sign out everywhere"}
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={closeSignOutAll}
                        disabled={isSigningOutAll}
                    >
                        Cancel
                    </button>
                </div>
            </Modal>
        </div>
    )
}
